import React, { useEffect } from 'react';
import { string, bool, func } from 'prop-types';
import { SortAscending, SortDescending, EllipsisVertical, Null } from '@nike/nike-design-system-icons';
import { TextStyled, TextFieldStyled, ButtonStyled, useTokens, Box } from '@nike/nike-design-system-components';
import styled from '@emotion/styled';
import { nike } from '@nike/nike-design-tokens';

import { sortHighlightColor } from './constants';

const { podiumCdsColorBgPrimary, podiumCdsColorGrey300 } = nike;

const Menu = styled.div`
  background: ${podiumCdsColorBgPrimary};
  border-radius: 4px;
  box-shadow: 0 0 0 1px ${podiumCdsColorGrey300};
  overflow: auto;
  position: absolute;
  right: ${({ align }) => (align === 'right' ? 'auto' : 0)};
  top: 35px;
  width: 260px;
`;

const MenuBody = styled.div`
  padding: 15px;
`;

const ColumnFilterSortMenu = ({
  align,
  disableFilter,
  disableSort,
  filterValue,
  handleClearColumnFilter,
  header,
  isFiltering,
  onFilterChange,
  onKeyDown,
  isMenuOpen,
  setIsMenuOpen,
  isEllipsisIconVisible,
  toggleSortAsc,
  toggleSortDesc,
  isSortAsc,
  isSortDesc,
  closeIconAndMenu,
  searchHelperText
}) => {
  const tokens = useTokens();
  const sortAscColor = isSortAsc ? sortHighlightColor : tokens.colorContentPrimary;
  const sortDescColor = isSortDesc ? sortHighlightColor : tokens.colorContentPrimary;
  const handleClick = event => {
    if (!event.target.closest('#columnHeaderMenu')) {
      closeIconAndMenu();
    }
  };

  useEffect(() => {
    document.body.addEventListener('click', handleClick);

    return () => {
      document.body.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <Box id='columnHeaderMenu' style={{ position: 'relative', zIndex: '1000' }}>
      <div
        style={{
          height: '18px',
          width: '18px',
          visibility: isEllipsisIconVisible ? 'visible' : 'hidden',
          cursor: 'pointer'
        }}
        onClick={() => setIsMenuOpen(isOpen => !isOpen)}
      >
        <EllipsisVertical data-testid={`${header}-columnHeaderMenuIcon`} />
      </div>
      {isMenuOpen && (
        <Menu align={align} data-testid={`${header}-columnHeaderMenu`}>
          <MenuBody>
            <Box>
              <TextStyled appearance='heading5' bold>
                Sort
              </TextStyled>
              <hr />
            </Box>
            <div
              style={{
                cursor: disableSort ? 'hover' : 'pointer',
                width: 'fit-content',
                display: 'flex',
                marginTop: '10px'
              }}
              onClick={disableSort ? () => {} : toggleSortAsc}
            >
              {disableSort ? (
                <Null data-testid={`${header}-sortAscDisabledIcon`} />
              ) : (
                <SortAscending color={sortAscColor} />
              )}
              <TextStyled appearance='body3' style={{ marginLeft: '5px', color: sortAscColor }}>
                Ascending
              </TextStyled>
            </div>
            <div
              style={{
                cursor: disableSort ? 'hover' : 'pointer',
                width: 'fit-content',
                display: 'flex',
                marginTop: '5px'
              }}
              onClick={disableSort ? () => {} : toggleSortDesc}
            >
              {disableSort ? (
                <Null data-testid={`${header}-sortDescDisabledIcon`} />
              ) : (
                <SortDescending color={sortDescColor} />
              )}
              <TextStyled appearance='body3' style={{ marginLeft: '5px', color: sortDescColor }}>
                Descending
              </TextStyled>
            </div>

            <Box mt='10px'>
              <TextStyled appearance='heading5' bold>
                Filter
              </TextStyled>
              <hr />
            </Box>

            <Box mt='10px' pr='10px'>
              <TextFieldStyled
                label='Search'
                onChange={onFilterChange}
                onKeyDown={onKeyDown}
                readOnly={disableFilter}
                value={filterValue}
              />

              {searchHelperText && (
                <TextStyled appearance='body3' style={{ marginBottom: '10px' }}>
                  {searchHelperText}
                </TextStyled>
              )}
            </Box>

            {isFiltering && (
              <div
                style={{ display: 'flex', flexDirection: 'row-reverse', paddingRight: '5px', paddingBottom: '10px' }}
              >
                <ButtonStyled
                  appearance='text'
                  background='light'
                  ariaLabel='Clear Filter'
                  type='button'
                  onClick={handleClearColumnFilter}
                >
                  Clear Filter
                </ButtonStyled>
              </div>
            )}
          </MenuBody>
        </Menu>
      )}
    </Box>
  );
};

ColumnFilterSortMenu.propTypes = {
  align: string,
  closeIconAndMenu: func.isRequired,
  disableFilter: bool,
  disableSort: bool,
  filterValue: string,
  handleClearColumnFilter: func.isRequired,
  header: string,
  isEllipsisIconVisible: bool,
  isFiltering: bool,
  isMenuOpen: bool,
  isSortAsc: bool,
  isSortDesc: bool,
  onFilterChange: func.isRequired,
  onKeyDown: func.isRequired,
  searchHelperText: string,
  setIsMenuOpen: func.isRequired,
  toggleSortAsc: func.isRequired,
  toggleSortDesc: func.isRequired
};

ColumnFilterSortMenu.defaultProps = {
  align: 'left',
  disableFilter: false,
  disableSort: false,
  filterValue: '',
  header: '',
  isEllipsisIconVisible: false,
  isFiltering: false,
  isMenuOpen: false,
  isSortAsc: false,
  isSortDesc: false,
  searchHelperText: ''
};

export default ColumnFilterSortMenu;
